import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { RefreshCw, Award, DollarSign } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AffiliatesTab = ({ adminFetch }) => {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [settings, setSettings] = React.useState({ dollars_per_point: 50, point_value_dollars: 0 });
  const [editSettings, setEditSettings] = React.useState(false);
  const [payoutModal, setPayoutModal] = React.useState(null);
  const [payoutAmount, setPayoutAmount] = React.useState('');
  const [payoutNote, setPayoutNote] = React.useState('');
  const [assignModal, setAssignModal] = React.useState(null);
  const [assignEmail, setAssignEmail] = React.useState('');
  const [assignName, setAssignName] = React.useState('');
  const [expandedRow, setExpandedRow] = React.useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await adminFetch(`${API_URL}/api/admin/affiliate-dashboard`);
      const d = await res.json();
      setData(d);
      if (d.settings) setSettings(d.settings);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  React.useEffect(() => { fetchData(); }, []);

  const handleSaveSettings = async () => {
    try {
      await adminFetch(`${API_URL}/api/admin/affiliate-settings`, {
        method: 'PUT',
        body: JSON.stringify(settings)
      });
      toast.success('Settings saved');
      setEditSettings(false);
      fetchData();
    } catch { toast.error('Failed to save settings'); }
  };

  const handlePayout = async () => {
    if (!payoutAmount || parseFloat(payoutAmount) <= 0) { toast.error('Enter a valid amount'); return; }
    try {
      await adminFetch(`${API_URL}/api/admin/affiliate-credits/payout`, {
        method: 'POST',
        body: JSON.stringify({
          customer_email: payoutModal.customer_email,
          amount: parseFloat(payoutAmount),
          code: payoutModal.code,
          note: payoutNote
        })
      });
      toast.success(`$${payoutAmount} credit issued to ${payoutModal.customer_email}`);
      setPayoutModal(null);
      setPayoutAmount('');
      setPayoutNote('');
      fetchData();
    } catch { toast.error('Payout failed'); }
  };

  // Snapshot the affiliate's current earned/paid totals as a "settled" baseline
  // (or clear it) so only NEW earnings count toward Available for Payout.
  const handleSettle = async (aff, reset = false) => {
    const msg = reset
      ? `Clear the settled baseline for ${aff.code}? Available for Payout will revert to lifetime earned − lifetime paid.`
      : `Mark ${aff.code}'s balance as settled to date?\n\nEarned: $${(aff.earned_credit || 0).toFixed(2)}  •  Paid: $${(aff.credits_paid || 0).toFixed(2)}\n\nFrom now on, every NEW referral order's credit becomes immediately available to pay out.`;
    if (!window.confirm(msg)) return;
    try {
      await adminFetch(`${API_URL}/api/admin/affiliate-credits/settle`, {
        method: 'POST',
        body: JSON.stringify({
          code: aff.code,
          reset,
          earned_credit: aff.earned_credit || 0,
          credits_paid: aff.credits_paid || 0
        })
      });
      toast.success(reset ? `Baseline cleared for ${aff.code}` : `${aff.code} settled — new earnings are now payable`);
      fetchData();
    } catch { toast.error('Failed to update settled balance'); }
  };

  const handleAssign = async () => {
    if (!assignEmail.trim()) { toast.error('Enter customer email'); return; }
    try {
      await adminFetch(`${API_URL}/api/admin/discount-codes/${assignModal.code_id}`, {
        method: 'PUT',
        body: JSON.stringify({
          assigned_customer_email: assignEmail.trim(),
          assigned_customer_name: assignName.trim()
        })
      });
      toast.success(`Code ${assignModal.code} assigned to ${assignEmail}`);
      setAssignModal(null);
      setAssignEmail('');
      setAssignName('');
      fetchData();
    } catch { toast.error('Failed to assign'); }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><RefreshCw className="h-8 w-8 text-teal-600 animate-spin" /></div>;
  if (!data) return <p className="text-slate-500">Failed to load data.</p>;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card><CardContent className="p-4">
          <p className="text-sm text-slate-500">Total Affiliate Revenue</p>
          <p className="text-2xl font-bold text-slate-900" data-testid="aff-total-revenue">${data.totals.total_revenue.toLocaleString()}</p>
          <p className="text-xs text-slate-400">{data.totals.total_orders} orders</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-sm text-slate-500">Total Points Earned</p>
          <p className="text-2xl font-bold text-teal-600" data-testid="aff-total-points">{data.totals.total_points}</p>
          <p className="text-xs text-slate-400">${settings.dollars_per_point} per point</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-sm text-slate-500">Credits Paid Out</p>
          <p className="text-2xl font-bold text-amber-600" data-testid="aff-credits-paid">${data.totals.total_credits_paid.toLocaleString()}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-sm text-slate-500">Active Affiliates</p>
          <p className="text-2xl font-bold text-slate-900">{data.affiliates.length}</p>
        </CardContent></Card>
      </div>

      {/* Point System Settings */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Point System Settings</CardTitle>
            <Button variant="outline" size="sm" onClick={() => setEditSettings(!editSettings)}>
              {editSettings ? 'Cancel' : 'Edit'}
            </Button>
          </div>
        </CardHeader>
        {editSettings && (
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Dollars per Point ($ spent to earn 1 point)</Label>
                <Input type="number" min="1" value={settings.dollars_per_point}
                  onChange={(e) => setSettings({...settings, dollars_per_point: parseFloat(e.target.value) || 50})}
                  data-testid="aff-dollars-per-point" />
              </div>
              <div>
                <Label>Point Value ($) (store credit per point)</Label>
                <Input type="number" min="0" step="0.01" value={settings.point_value_dollars}
                  onChange={(e) => setSettings({...settings, point_value_dollars: parseFloat(e.target.value) || 0})}
                  data-testid="aff-point-value" />
              </div>
            </div>
            <p className="text-xs text-slate-500">
              Example: With ${settings.dollars_per_point}/point and ${settings.point_value_dollars}/point value, 
              a customer who generates ${settings.dollars_per_point * 10} in sales earns 10 points = ${(10 * settings.point_value_dollars).toFixed(2)} credit.
            </p>
            <Button onClick={handleSaveSettings} className="bg-teal-600 hover:bg-teal-700" data-testid="aff-save-settings">
              Save Settings
            </Button>
          </CardContent>
        )}
      </Card>

      {/* Affiliate Leaderboard */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Award className="h-5 w-5 text-amber-500" /> Affiliate Leaderboard
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.affiliates.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-slate-500 mb-2">No affiliate codes assigned yet.</p>
              <p className="text-sm text-slate-400">Go to Discount Codes tab, edit a code, and assign it to a customer.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left">
                    <th className="p-2 font-medium text-slate-500">Code</th>
                    <th className="p-2 font-medium text-slate-500">Customer</th>
                    <th className="p-2 font-medium text-slate-500 text-right">Orders</th>
                    <th className="p-2 font-medium text-slate-500 text-right">Revenue</th>
                    <th className="p-2 font-medium text-slate-500 text-right">Ref. Pts</th>
                    <th className="p-2 font-medium text-slate-500 text-right">Purch. Pts</th>
                    <th className="p-2 font-medium text-slate-500 text-right">Total Pts</th>
                    <th className="p-2 font-medium text-slate-500 text-right">Credit Earned</th>
                    <th className="p-2 font-medium text-slate-500 text-right">Paid Out</th>
                    <th className="p-2 font-medium text-slate-500 text-right">Available</th>
                    <th className="p-2 font-medium text-slate-500 text-right">Store Credit</th>
                    <th className="p-2 font-medium text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.affiliates.map((aff, idx) => (
                    <React.Fragment key={aff.code}>
                      <tr className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer ${expandedRow === aff.code ? 'bg-teal-50' : ''}`}
                          onClick={() => setExpandedRow(expandedRow === aff.code ? null : aff.code)}>
                        <td className="p-2">
                          <span className="font-mono font-bold text-teal-700 bg-teal-50 px-2 py-0.5 rounded">{aff.code}</span>
                          {aff.discount_type && <span className="ml-1 text-xs text-slate-400">({aff.discount_value}{aff.discount_type === 'percentage' ? '%' : '$'} off)</span>}
                        </td>
                        <td className="p-2">
                          <div className="font-medium text-slate-800">{aff.customer_name || 'Unassigned'}</div>
                          <div className="text-xs text-slate-400">{aff.customer_email}</div>
                        </td>
                        <td className="p-2 text-right font-medium">{aff.total_orders}</td>
                        <td className="p-2 text-right font-bold text-slate-900">${aff.total_revenue.toLocaleString()}</td>
                        <td className="p-2 text-right text-teal-600">{aff.referral_points || 0}</td>
                        <td className="p-2 text-right text-blue-600">{aff.purchase_points || 0}</td>
                        <td className="p-2 text-right font-bold text-teal-600">{aff.points}</td>
                        <td className="p-2 text-right">${aff.earned_credit.toFixed(2)}</td>
                        <td className="p-2 text-right text-amber-600">${aff.credits_paid.toFixed(2)}</td>
                        <td className="p-2 text-right font-bold text-green-600">${aff.available_credit.toFixed(2)}</td>
                        <td className="p-2 text-right font-bold text-teal-700" data-testid={`aff-store-credit-${aff.code}`}>
                          ${(aff.affiliate_store_credit || 0).toFixed(2)}
                        </td>
                        <td className="p-2">
                          <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                            <Button size="sm" variant="outline" className="text-xs h-7"
                              onClick={() => { setPayoutModal(aff); setPayoutAmount(aff.available_credit > 0 ? aff.available_credit.toFixed(2) : ''); }}
                              data-testid={`aff-payout-${aff.code}`}>
                              <DollarSign className="h-3 w-3 mr-1" /> Payout
                            </Button>
                            <Button size="sm" variant="outline" className="text-xs h-7"
                              onClick={() => { setAssignModal(aff); setAssignEmail(aff.customer_email || ''); setAssignName(aff.customer_name || ''); }}
                              data-testid={`aff-assign-${aff.code}`}>
                              Assign
                            </Button>
                          </div>
                        </td>
                      </tr>
                      {expandedRow === aff.code && (
                        <tr>
                          <td colSpan="12" className="p-3 bg-slate-50">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div className="md:col-span-2">
                                <div className="flex items-center justify-between mb-2">
                                  <p className="text-xs font-medium text-slate-500">Referral Orders ({aff.total_orders} total)</p>
                                  <div className="flex gap-2 text-xs">
                                    <span className="px-2 py-0.5 rounded bg-green-100 text-green-700 font-medium" data-testid={`aff-orders-with-credit-${aff.code}`}>
                                      {aff.orders_with_credit || 0} used credit
                                    </span>
                                    <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-600 font-medium">
                                      {aff.orders_without_credit || 0} no credit
                                    </span>
                                  </div>
                                </div>
                                {aff.recent_orders.length > 0 ? (
                                  <div className="space-y-1">
                                    {aff.recent_orders.map((o, i) => {
                                      const creditUsed = parseFloat(o.store_credit_used || 0);
                                      return (
                                        <div key={i} className="flex flex-wrap items-center gap-2 text-xs bg-white border border-slate-200 rounded px-2 py-1.5">
                                          <span className="font-mono font-medium text-slate-700">{o.order_id}</span>
                                          <span className="text-slate-500">${parseFloat(o.total).toFixed(2)}</span>
                                          <span className="text-slate-400">{new Date(o.created_at).toLocaleDateString()}</span>
                                          {o.customer_email && (
                                            <span className="text-slate-500 truncate max-w-[160px]" title={o.customer_email}>
                                              {o.customer_name || o.customer_email}
                                            </span>
                                          )}
                                          {creditUsed > 0 ? (
                                            <span className="ml-auto px-2 py-0.5 rounded bg-green-100 text-green-700 font-semibold" data-testid={`aff-order-credit-used-${o.order_id}`}>
                                              Credit: ${creditUsed.toFixed(2)}
                                            </span>
                                          ) : (
                                            <span className="ml-auto px-2 py-0.5 rounded bg-slate-100 text-slate-500 font-medium">
                                              No credit
                                            </span>
                                          )}
                                          <span
                                            className="px-2 py-0.5 rounded bg-teal-100 text-teal-700 font-semibold whitespace-nowrap"
                                            title="Credit this affiliate earned from this order (your point system)"
                                            data-testid={`aff-order-earned-credit-${o.order_id}`}
                                          >
                                            Affiliate earned: ${parseFloat(o.affiliate_earned_credit || 0).toFixed(2)}
                                          </span>
                                          {typeof o.customer_current_store_credit === 'number' && (
                                            <span className="text-slate-400 whitespace-nowrap" title="Buyer's current store credit balance">
                                              Buyer bal: ${o.customer_current_store_credit.toFixed(2)}
                                            </span>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                ) : <p className="text-xs text-slate-400">No referral orders yet</p>}
                                {aff.total_credit_used_on_orders > 0 && (
                                  <p className="text-xs text-slate-500 mt-2">
                                    Total credit applied across orders: <span className="font-semibold text-slate-700">${aff.total_credit_used_on_orders.toFixed(2)}</span>
                                  </p>
                                )}
                              </div>
                              <div className="space-y-3">
                                <div className="bg-teal-50 border border-teal-200 rounded p-2">
                                  <p className="text-xs font-medium text-teal-700">Affiliate Store Credit</p>
                                  <p className="text-lg font-bold text-teal-800" data-testid={`aff-detail-store-credit-${aff.code}`}>
                                    ${(aff.affiliate_store_credit || 0).toFixed(2)}
                                  </p>
                                  <p className="text-[10px] text-teal-600">Current balance in customer account</p>
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-slate-500 mb-1">Purchase Points</p>
                                  <p className="text-xs text-slate-600">{aff.purchase_orders || 0} own orders</p>
                                  <p className="text-xs text-slate-600">${(aff.purchase_revenue || 0).toFixed(2)} revenue</p>
                                  <p className="text-xs font-medium text-blue-600">{aff.purchase_points || 0} points earned</p>
                                  {(aff.purchase_orders_list && aff.purchase_orders_list.length > 0) && (
                                    <div className="mt-2 space-y-1" data-testid={`aff-own-orders-${aff.code}`}>
                                      <p className="text-[11px] font-medium text-slate-500">
                                        Own orders — store credit used{aff.purchase_credit_used_total > 0 ? `: $${aff.purchase_credit_used_total.toFixed(2)} across ${aff.purchase_orders_with_credit}` : ''}
                                      </p>
                                      {aff.purchase_orders_list.map((po, i) => {
                                        const used = parseFloat(po.store_credit_used || 0);
                                        return (
                                          <div key={i} className="flex items-center gap-2 text-[11px] bg-white border border-slate-200 rounded px-2 py-1">
                                            <span className="font-mono text-slate-600 truncate max-w-[110px]">{po.order_id}</span>
                                            <span className="text-slate-500">${parseFloat(po.total).toFixed(2)}</span>
                                            <span className="text-slate-400">{po.created_at ? new Date(po.created_at).toLocaleDateString() : ''}</span>
                                            {used > 0 ? (
                                              <span className="ml-auto px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-semibold whitespace-nowrap" data-testid={`aff-own-order-credit-${po.order_id}`}>
                                                Used credit: ${used.toFixed(2)}
                                              </span>
                                            ) : (
                                              <span className="ml-auto px-1.5 py-0.5 rounded bg-slate-100 text-slate-400 whitespace-nowrap">
                                                No credit used
                                              </span>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-slate-500 mb-1">Credit Payout History</p>
                                  {aff.credit_history.length > 0 ? aff.credit_history.map((c, i) => (
                                    <div key={i} className="text-xs text-slate-600 py-0.5">
                                      ${parseFloat(c.amount).toFixed(2)} - {new Date(c.created_at).toLocaleDateString()} {c.note && `(${c.note})`}
                                    </div>
                                  )) : <p className="text-xs text-slate-400">No payouts yet</p>}
                                </div>
                                <div className="border-t border-slate-200 pt-2">
                                  <p className="text-xs font-medium text-slate-500 mb-1">Settle Balance</p>
                                  {aff.is_settled ? (
                                    <>
                                      <p className="text-[11px] text-slate-500 mb-1.5">
                                        Settled on {aff.credit_settled_at ? new Date(aff.credit_settled_at).toLocaleDateString() : '—'}. Only earnings/payouts after that count toward Available.
                                      </p>
                                      <Button size="sm" variant="outline" className="text-xs h-7"
                                        onClick={() => handleSettle(aff, true)}
                                        data-testid={`aff-unsettle-${aff.code}`}>
                                        Clear settled baseline
                                      </Button>
                                    </>
                                  ) : (
                                    <>
                                      <p className="text-[11px] text-slate-500 mb-1.5">
                                        Mark past earnings/payouts as settled so each NEW referral order's credit becomes immediately payable.
                                      </p>
                                      <Button size="sm" variant="outline" className="text-xs h-7"
                                        onClick={() => handleSettle(aff, false)}
                                        data-testid={`aff-settle-${aff.code}`}>
                                        <DollarSign className="h-3 w-3 mr-1" /> Settle to date
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payout Modal */}
      {payoutModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Issue Credit Payout</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-slate-600">
                Issue store credit to <strong>{payoutModal.customer_name || payoutModal.customer_email}</strong> for code <strong>{payoutModal.code}</strong>
              </p>
              <p className="text-xs text-slate-500">Available: ${payoutModal.available_credit.toFixed(2)}</p>
              <div>
                <Label>Amount ($)</Label>
                <Input type="number" min="0.01" step="0.01" value={payoutAmount}
                  onChange={(e) => setPayoutAmount(e.target.value)}
                  data-testid="payout-amount" />
              </div>
              <div>
                <Label>Note (optional)</Label>
                <Input value={payoutNote} onChange={(e) => setPayoutNote(e.target.value)}
                  placeholder="e.g., Q1 2026 affiliate payout" data-testid="payout-note" />
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={handlePayout} className="bg-teal-600 hover:bg-teal-700 flex-1" data-testid="payout-confirm">
                  Approve Payout
                </Button>
                <Button variant="outline" onClick={() => setPayoutModal(null)}>Cancel</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Assign Modal */}
      {assignModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Assign Code to Customer</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-slate-600">Assign code <strong>{assignModal.code}</strong> to a customer for affiliate tracking.</p>
              <div>
                <Label>Customer Name</Label>
                <Input value={assignName} onChange={(e) => setAssignName(e.target.value)}
                  placeholder="e.g., John Smith" data-testid="assign-name" />
              </div>
              <div>
                <Label>Customer Email</Label>
                <Input value={assignEmail} onChange={(e) => setAssignEmail(e.target.value)}
                  placeholder="e.g., john@example.com" data-testid="assign-email" />
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={handleAssign} className="bg-teal-600 hover:bg-teal-700 flex-1" data-testid="assign-confirm">
                  Assign
                </Button>
                <Button variant="outline" onClick={() => setAssignModal(null)}>Cancel</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AffiliatesTab;
